
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { AssessmentQuestion } from './AssessmentQuestion';

interface MarketImpactSectionProps {
  onComplete: (data: { score: number; responses: Record<string, number> }) => void;
  initialData?: { score: number; responses: Record<string, number> };
}

export const MarketImpactSection = ({ onComplete, initialData }: MarketImpactSectionProps) => {
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>(
    initialData?.responses || {}
  );

  const questions = [
    {
      id: 'marketUnderstanding',
      title: 'Market Understanding',
      question: 'How do you develop market strategy?',
      options: [
        { text: 'I understand our market position and track competitive moves. I know our product\'s place in the market landscape.', points: 2 },
        { text: 'I identify valuable market opportunities and articulate why they matter. I analyze market gaps and validate potential areas for growth.', points: 4 },
        { text: 'I develop comprehensive market strategies that guide product direction. I map complex market dynamics and position our products effectively.', points: 7 },
        { text: 'I create entirely new markets or categories. I identify white space that others miss and successfully pioneer new territory for the business.', points: 10 }
      ],
      evidence: [
        { 
          title: 'Market Analysis',
          description: 'Share an example of your market analysis approach'
        },
        {
          title: 'Opportunity Identification',
          description: 'How did you identify a key market opportunity?'
        }
      ]
    },
    {
      id: 'businessImpact',
      title: 'Business Impact',
      question: 'How do you drive business results?',
      options: [
        { text: 'I contribute to business goals and understand how my product work impacts key metrics. I align my priorities with business objectives.', points: 2 },
        { text: 'I achieve revenue or growth targets for my product area. I effectively translate product work into measurable business value.', points: 4 },
        { text: 'I create new revenue streams or business models. I develop untapped ways to monetize products or reach new customer segments.', points: 7 },
        { text: 'I transform how the business creates value. My product initiatives fundamentally change the company\'s market position or business model.', points: 10 }
      ],
      evidence: [
        {
          title: 'Business Impact',
          description: 'Describe a significant business impact you drove through product'
        },
        {
          title: 'Measurement Approach',
          description: 'How did you measure and demonstrate this business impact?'
        }
      ]
    }
  ];

  const handleSelect = (points: number) => {
    const currentQuestionId = questions[currentQuestion].id;
    setResponses(prev => ({
      ...prev,
      [currentQuestionId]: points
    }));
  };

  const calculateScore = (responses: Record<string, number>): number => {
    return Object.values(responses).reduce((sum, points) => sum + points, 0);
  };

  const handleNext = () => {
    if (currentQuestion === questions.length - 1) {
      const finalData = {
        score: calculateScore(responses),
        responses
      };
      
      toast({
        title: "Section completed",
        description: "Your Market & Business Impact assessment has been saved.",
      });
      
      onComplete(finalData);
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestion(prev => Math.max(0, prev - 1));
  };

  const currentQuestionData = questions[currentQuestion];
  const selectedPoints = responses[currentQuestionData.id];

  return (
    <Card className="max-w-3xl mx-auto">
      <AssessmentQuestion
        title={currentQuestionData.title}
        question={currentQuestionData.question}
        options={currentQuestionData.options}
        evidence={currentQuestionData.evidence}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSelect={handleSelect}
        selectedPoints={selectedPoints}
        showPrevious={currentQuestion > 0}
      />
    </Card>
  );
};

export default MarketImpactSection;
